import { push, replace } from 'connected-react-router';
import cloneDeep from 'lodash.clonedeep';
import { throttle, call, put, takeLatest, takeEvery, select, race, take, fork, delay } from 'redux-saga/effects';
import { APPLICATION_DISPLAY_NAME } from 'JS/config/config';
import { FREE_ELEMENTS_LIMIT } from 'JS/config/defaults';
import { ROUTE_CODES } from 'JS/config/routes';
import preloadScribeFonts, { preloadAllScribesFonts } from 'JS/shared/helpers/preloadScribeFonts';
import { trackOpenUpgradeModal } from 'JS/actionCreators/trackingActions';
import { getProviderForType } from 'JS/shared/providers';
import ScribeImageElementModel from 'JS/models/ScribeImageElementModel';

import { appServices } from '../shared/helpers/app-services/AppServices';
import { getElementIndexById } from '../shared/helpers/scribeHelper';
import ScribeProvider from '../shared/providers/ScribeProvider';
import ScribeModel from '../models/ScribeModel';
import ScribeTextElementModel from '../models/ScribeTextElementModel';
import ScribeShapeElementModel from '../models/ScribeShapeElementModel';
import { fitElementToCanvas } from '../shared/helpers/element';
import { getTextDimensions } from '../shared/helpers/text-draw';
import { getScribeLink } from '../shared/helpers/linkHelper';
import { rateLimitedfetch } from '../shared/helpers/requestLimiter';
import {
  ACCOUNT_TYPES,
  CURSOR_WHITELIST,
  maxLibraryImageUploadRetries,
  maxUserImageUploadRetries,
  maxImageUploadOnScribeLoadRetries
} from '../config/consts';
import {
  UPDATE_SCRIBE,
  CREATE_ELEMENT,
  CREATE_TEXT_ELEMENT,
  CREATE_SHAPE_ELEMENT,
  CREATE_IMAGE_ELEMENT,
  DELETE_ELEMENT_BY_ID,
  DELETE_MULTIPLE_ELEMENTS_BY_ID,
  DELETE_ACTIVE_ELEMENT,
  MOVE_ELEMENT_ON_TIMELINE,
  LOAD_SCRIBES,
  DELETE_SCRIBE,
  SORT_SCRIBES,
  ADD_SCRIBE_REQUESTED,
  updateScribeFailed,
  updateScribeSuccess,
  setActiveElements,
  updateScribe as updateScribeAction,
  createElement as createElementAction,
  deleteMultipleElementsById as deleteMultipleElementsByAction,
  loadScribes as loadScribesAction,
  loadScribesSuccess,
  deleteScribeSuccess,
  addScribeSuccess,
  SAVE_AUDIO,
  DELETE_AUDIO,
  UPDATE_AUDIO_CONFIG,
  LOAD_SCRIBE_BY_ID,
  setActiveScribe,
  LOAD_CURSORS,
  loadCursorsSuccess,
  UPDATE_SCRIBE_CURSOR,
  LOCK_ELEMENTS,
  UNLOCK_ELEMENTS,
  UNLOCK_ACTIVE_ELEMENTS,
  unlockElements as unlockElementsAction,
  lockElements as lockElementsAction,
  LOCK_ACTIVE_ELEMENTS,
  TOGGLE_GRID,
  UPDATE_SCRIBE_TITLE,
  SET_ELEMENTS_TIMINGS,
  SET_ACTIVE_SCRIBE,
  prepScribeForEditorSuccess,
  CREATE_SCENE,
  UPDATE_SCENE_THUMBNAIL,
  UPDATE_SCENE_ELEMENTS,
  DUPLICATE_SELECTED_SCENES,
  DELETE_SELECTED_SCENES,
  setSelectedScenes as setSelectedScenesAction,
  UPDATE_SCENE,
  CHANGE_SCENE_ORDER,
  updateClipboardPayload,
  setActiveScene,
  deleteSelectedScenes as deleteSelectedScenesAction,
  saveAudioSuccess,
  saveAudioFailed,
  LOAD_SCRIBES_SUCCESS,
  UPLOAD_LIBRARY_IMAGE_ASSET,
  uploadImageAssetOnLoad as uploadImageAssetOnLoadAction,
  uploadImageAssetSuccess,
  uploadImageAssetFailed,
  UPLOAD_IMAGE_ASSET_ON_LOAD,
  UPLOAD_USER_IMAGE_ASSET,
  updateUndoRedoHistory
} from '../actionCreators/scribeActions';
import {
  EDITOR_PASTE,
  EDITOR_CUT,
  EDITOR_UNDO,
  EDITOR_REDO,
  editorCopy as copyAction,
  editorUndoSuccess,
  editorRedoSuccess
} from '../actionCreators/editorActions';
import { showError, showCallout, hideModal } from '../actionCreators/uiActions';
import { reauth } from '../actionCreators/authActions';
import { sendErrorToSentry } from '../logging';

import { getScribeById, getClipboard, getActiveElements, getUndoObjects, getUserAccountType } from './selectors';
import { isReauthableError } from './helpers';
import { duplicateScenesIntoScribe } from './sagaHelpers/duplicateScenesIntoScribe';
import deleteScenesFromScribe from './sagaHelpers/deleteScenesFromScribe';
import createNewScene from './sagaHelpers/createNewScene';
import replaceActiveScene from './sagaHelpers/replaceActiveScene';
import multiDragAwareReorder from './sagaHelpers/multiDragAwareReorder';
import pasteElementsIntoScribe from './sagaHelpers/pasteElementsIntoScribe';
import pasteScenesIntoScribe from './sagaHelpers/pasteScenesIntoScribe';
import applyImageRecolor from './sagaHelpers/applyImageRecolor';
import duplicateScribeSagas from './scribeSagas/duplicateScribeSagas';
import sanitizeScribeForTransport from './sagaHelpers/sanitizeScribeForTransport';
import {
  replaceImageUrlInScribe,
  replaceImageUrlInHistory,
  replaceImageUrlBrokenImageInScribe,
  replaceBrokenImageUrlInHistory
} from './sagaHelpers/replaceImageUrl';

const scribeProvider = new ScribeProvider();

function* updateScribe(action) {
  try {
    const payloadScribe = sanitizeScribeForTransport(action.scribe);

    // Update the scribe in the database
    const scribe = yield call(appServices.projects.updateProject, payloadScribe);

    yield put(updateScribeSuccess(scribe));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(updateScribeFailed(error.message));

    // If the token has expired app-services returns 400 rather than 401 🙄
    if (isReauthableError(error)) {
      yield put(reauth(action));
    }
  }
}

export function* addScribe(action) {
  try {
    const scribe = yield call(
      appServices.projects.createProject,
      new ScribeModel({
        title: action.title,
        canvasSize: action.canvasSize,
        createdOn: action.date,
        updatedOn: action.date,
        source: action.source,
        rawElements: action.elements,
        thumbnailImage: action.thumbnail,
        settings: action.settings,
        cursor: action.cursor,
        scenes: action.scenes,
        audioClips: action.audioClips
      })
    );
    yield put(addScribeSuccess(scribe));
    yield put(hideModal(scribe.id));

    if (action.replaceHistory) {
      yield put(replace(getScribeLink(scribe)));
    } else {
      yield put(push(getScribeLink(scribe)));
    }
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
    if (isReauthableError(error)) {
      yield put(reauth(action));
    }
  }
}

function* createTextElement(action) {
  const { scribeId, canvasSize } = action;
  yield select(getScribeById, scribeId);
  const elem = new ScribeTextElementModel({ canvasSize });
  elem.text = resx('new-text-element-default-text');

  const { height, width } = getTextDimensions(elem);
  const { x, y, scaleX, scaleY } = fitElementToCanvas(width, height, canvasSize);
  elem.scaleX = scaleX;
  elem.scaleY = scaleY;
  elem.x = x;
  elem.y = y;

  yield put(createElementAction(elem, scribeId, canvasSize));
}

function* createShapeElement(action) {
  const { scribeId, shapeType, canvasSize } = action;
  const scribe = yield select(getScribeById, scribeId);
  const elem = new ScribeShapeElementModel({ shapeType, canvasSize });

  yield put(createElementAction(elem, scribeId, canvasSize));
  yield put(push(getScribeLink(scribe)));
}

function* createImageElement(action) {
  const { scribeId, canvasSize, initialData = {} } = action;
  const newElement = Object.assign(new ScribeImageElementModel({ canvasSize }), initialData);

  yield applyImageRecolor(newElement);

  yield put(createElementAction(newElement, scribeId, canvasSize));
}

function* createElement(action) {
  const { scribeId } = action;

  const activeScribe = yield select(getScribeById, scribeId);
  const newActiveScribe = cloneDeep(activeScribe);
  const newElement = cloneDeep(action.element);
  const activeScene = newActiveScribe.scenes.find(scene => scene.active);

  // Add the elements to the scribe
  newActiveScribe.elements.push(newElement);

  // Add element id to active scene
  activeScene.elementIds = [...activeScene.elementIds, newElement.id];

  yield put(setActiveElements([newElement.id], true));

  // Update the scribe
  yield put(updateScribeAction(newActiveScribe));
}

function* userCanAddElements(scribeId, clipboard) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const elementsInCurrentScribe = scribe.elements.length;
  const elementsInClipboard = clipboard.data.length;

  const userAccountType = yield select(getUserAccountType);

  return elementsInCurrentScribe + elementsInClipboard <= FREE_ELEMENTS_LIMIT || userAccountType !== ACCOUNT_TYPES.FREE;
}

function* handleEditorPaste({ scribeId, inPlace }) {
  const scribe = yield select(getScribeById, scribeId);
  const clipboardPayload = yield select(getClipboard);

  if (!clipboardPayload) {
    return;
  }

  if (clipboardPayload.type === 'scene') {
    if (clipboardPayload.metadata.canvasSize !== scribe.canvasSize) return;

    const selectedSceneIds = yield select(state => state.scribes.selectedSceneIds);
    const { updatedScribe, lastSceneId } = pasteScenesIntoScribe(scribe, clipboardPayload, selectedSceneIds);
    yield put(updateScribeAction(updatedScribe));
    yield put(setActiveScene(scribe.id, lastSceneId));
  }

  if (clipboardPayload.type === 'element') {
    const userCanAddElement = yield call(userCanAddElements, scribeId, clipboardPayload);
    if (userCanAddElement) {
      const activeElementIds = yield select(getActiveElements);
      const { scribe: updatedScribe, newElementIds, clipboard: updatedClipboardPayload } = pasteElementsIntoScribe(
        scribe,
        clipboardPayload,
        activeElementIds,
        inPlace || clipboardPayload.metadata.wasCut
      );

      yield put(updateScribeAction(updatedScribe));
      yield put(setActiveElements(newElementIds));
      yield put(updateClipboardPayload(updatedClipboardPayload));
    } else {
      yield put(trackOpenUpgradeModal('Element Limit'));
      yield put(push(`${ROUTE_CODES.EDITOR.replace(':scribeId', scribeId)}${ROUTE_CODES.UPGRADE}`));
    }
  }
}

function* deleteElementById({ elementId, scribeId }) {
  const activeScribe = yield select(getScribeById, scribeId);
  const newActiveScribe = cloneDeep(activeScribe);
  const indexToRemove = getElementIndexById(elementId, newActiveScribe);
  // If the index is -1 then the element isnt in the scribe so return and do nothing
  if (indexToRemove === -1) return;

  // Remove the active element from the list of elements
  newActiveScribe.elements.splice(indexToRemove, 1);
  newActiveScribe.activeElements = [];

  yield put(updateScribeAction(newActiveScribe));
}

function* deleteMultipleElementsById({ elements: elementIds, scribeId }) {
  const activeScribe = yield select(getScribeById, scribeId);
  const newActiveScribe = cloneDeep(activeScribe);
  // elements might be a string, so convert it to an array
  [].concat(elementIds).forEach(element => {
    const indexToRemove = getElementIndexById(element, newActiveScribe);
    if (indexToRemove !== -1) {
      newActiveScribe.elements.splice(indexToRemove, 1);
    }
  });

  newActiveScribe.scenes.forEach(scene => {
    scene.elementIds = scene.elementIds.filter(elId => !elementIds.includes(elId));
  });

  yield put(updateScribeAction(newActiveScribe));
}

function* cut({ scribeId }) {
  const scribe = yield select(getScribeById, scribeId);
  const activeElementIds = yield select(getActiveElements);
  const selectedSceneIds = yield select(state => state.scribes.selectedSceneIds);
  const hasScenesSelected = !!selectedSceneIds.length;
  const hasElementsSelected = !!activeElementIds.length;

  if (
    !scribe ||
    (!hasScenesSelected && !hasElementsSelected) ||
    (hasScenesSelected && !(selectedSceneIds.length < scribe.scenes.length))
  )
    return;

  yield put(copyAction(scribeId, true));

  if (hasScenesSelected) {
    yield put(deleteSelectedScenesAction(scribeId));
  } else {
    yield put(setActiveElements([]));
    yield put(deleteMultipleElementsByAction(activeElementIds, scribeId));
  }
}

function* undo(action) {
  const { scribeId } = action;
  const { activeScribe, activeScribePast, activeScribeFuture } = yield select(getUndoObjects, scribeId);

  // Do nothing if there is nothing in the undo history
  if (!activeScribePast.length) return;

  const newFuture = [cloneDeep(activeScribe), ...activeScribeFuture];
  const newActiveScribe = activeScribePast[activeScribePast.length - 1];

  const newPast = activeScribePast.slice(0, activeScribePast.length - 1);

  yield put(editorUndoSuccess(newPast, newFuture));
  yield put(updateScribeAction(newActiveScribe, true));
}

function* redo(action) {
  const { scribeId } = action;
  const { activeScribe, activeScribePast, activeScribeFuture } = yield select(getUndoObjects, scribeId);

  // Do nothing if there is nothing in the undo future
  if (!activeScribeFuture.length) return;
  const newActiveScribe = activeScribeFuture[0];
  const newFuture = activeScribeFuture.slice(1);
  const newPast = [...activeScribePast, cloneDeep(activeScribe)];

  yield put(editorRedoSuccess(newPast, newFuture));
  yield put(updateScribeAction(newActiveScribe, true));
}

function* moveElementOnTimeline({ scribeId, source, destination, draggableId }) {
  const activeScribe = yield select(getScribeById, scribeId);
  const activeElements = yield select(getActiveElements);
  const activeScene = activeScribe.scenes.find(scene => scene.active);
  const elementIdsList = activeScene.elementIds.map(id => ({ id }));

  const newActiveScribe = cloneDeep(activeScribe);
  const newActiveScene = newActiveScribe.scenes.find(scene => scene.active);

  const updatedElements = multiDragAwareReorder(elementIdsList, source, destination, draggableId, activeElements);

  newActiveScene.elementIds = updatedElements.map(element => {
    return element.id;
  });

  yield put(updateScribeAction(newActiveScribe));
}

function* deleteActiveElement({ scribeId }) {
  const activeElements = yield select(getActiveElements);

  yield put(deleteMultipleElementsByAction(activeElements, scribeId));
}

function* loadScribes(action) {
  try {
    const query = action.query || `?sortBy=modifiedDate&sortOrder=desc`;

    const data = yield call(scribeProvider.getScribes, query);
    const userAccountType = yield select(getUserAccountType);

    const scribes = data.map(scribe => {
      if (userAccountType && userAccountType !== ACCOUNT_TYPES.FREE) {
        // The view only flag from the API is determined by a user's session and when a user has upgraded the
        // session isn't renewed until they log out and log in again.
        // This ensures when the user upgrades they are able to edit their scribes in their current session
        scribe.viewOnly = false;
      }
      const model = ScribeModel.fromObject(scribe);
      model.lastSavedVersion = scribe.version;
      return model;
    });

    yield call(preloadAllScribesFonts, scribes);

    yield put(loadScribesSuccess(scribes));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
    if (isReauthableError(error)) {
      yield put(reauth(action));
    }
  }
}

function* deleteScribe(action) {
  try {
    yield call(scribeProvider.deleteScribe, action.scribe);
    yield put(deleteScribeSuccess(action.scribe.id, action.scribe.title));
    yield put(loadScribesAction());
    yield put(
      showCallout({
        title: resx('action-deletescribe-success-callout-title-text'),
        details: action.scribe.title
      })
    );
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
    if (isReauthableError(error)) {
      yield put(reauth(action));
    }
  }
}

function* sortScribes(action) {
  try {
    let sortBy = 'title';
    let sortOrder;
    switch (action.sortOrder) {
      case 'alphaAscend':
        sortOrder = 'asc';
        break;
      case 'alphaDescend':
        sortOrder = 'desc';
        break;
      default:
        sortBy = 'modifiedDate';
        sortOrder = 'desc';
    }
    const queryParams = `?sortBy=${sortBy}&sortOrder=${sortOrder}`;

    yield call(loadScribes, { query: queryParams });
  } catch (error) {
    sendErrorToSentry(error);
    yield put(showError(error));
    if (isReauthableError(error)) {
      yield put(reauth(action));
    }
  }
}

export function* saveAudio({ audio, scribeId, source, audioType, volume = 1, fileName }) {
  try {
    const assetId = yield call(appServices.uploadAsset, audio, fileName);

    const audioConfig = {
      assetId,
      type: audioType,
      volume,
      source,
      fileName
    };

    const activeScribe = yield select(getScribeById, scribeId);
    const newActiveScribe = cloneDeep(activeScribe);

    newActiveScribe.audioClips.push(audioConfig);
    yield put(updateScribeAction(newActiveScribe));
    yield put(saveAudioSuccess(audioType));
  } catch (error) {
    yield put(saveAudioFailed(audioType));
    yield put(showError(error));
  }
}

export function* updateAudioConfig({ scribeId, audioType, config }) {
  const activeScribe = yield select(getScribeById, scribeId);
  const newActiveScribe = cloneDeep(activeScribe);
  const audioIndexToReplace = newActiveScribe.audioClips.findIndex(clip => clip.type === audioType);
  const audioClip = {
    ...newActiveScribe.audioClips[audioIndexToReplace],
    ...config
  };
  newActiveScribe.audioClips.splice(audioIndexToReplace, 1, audioClip);

  yield put(updateScribeAction(newActiveScribe, true));
}

export function* deleteAudio({ scribeId, audioType }) {
  const activeScribe = yield select(getScribeById, scribeId);
  const audioAssetId = activeScribe.audioClips.find(clip => clip.type === audioType).assetId;

  const newActiveScribe = cloneDeep(activeScribe);

  const audioIndexToRemove = newActiveScribe.audioClips.findIndex(clip => clip.type === audioType);

  newActiveScribe.audioClips.splice(audioIndexToRemove, 1);
  yield put(updateScribeAction(newActiveScribe));

  yield call(appServices.deleteAsset, audioAssetId);
}

export function* loadScribeById({ scribeId, setToActiveOnLoad = true }) {
  const scribeObject = yield call(appServices.projects.getProject, scribeId);
  const userAccountType = yield select(getUserAccountType);

  if (userAccountType && userAccountType !== ACCOUNT_TYPES.FREE) {
    // The view only flag from the API is determined by a user's session and when a user has upgraded the
    // session isn't renewed until they log out and log in again.
    // This ensures when the user upgrades they are able to edit their scribes in their current session
    scribeObject.viewOnly = false;
  }

  const scribe = ScribeModel.fromObject(scribeObject);
  yield call(preloadScribeFonts, scribe);
  yield put(loadScribesSuccess([scribe]));

  if (setToActiveOnLoad) {
    yield put(setActiveScribe(scribeId));
  }
}

export function* loadCursors() {
  const cursors = yield call(appServices.getCursors);

  const whitelistedCursors = cursors.filter(cursor => {
    const attributes = new window.DOMParser()
      .parseFromString(cursor.metadata, 'text/xml')
      .getElementsByTagName('customHand')[0].attributes;

    const uniqueId = parseInt(attributes.uniqueID.value, 10);
    return CURSOR_WHITELIST.includes(uniqueId);
  });

  yield put(loadCursorsSuccess(whitelistedCursors));
}

function* updateScribeCursor({ scribeId, cursorId }) {
  const activeScribe = yield select(state => getScribeById(state, scribeId));
  const updatedScribe = {
    ...activeScribe,
    cursor: cursorId
  };
  yield put(updateScribeAction(updatedScribe));
}

function* updateElementsLock({ type, elementIds, scribeId }) {
  const lockFlag = type === LOCK_ELEMENTS;
  const elementsToUpdate = Array.isArray(elementIds) ? elementIds : [elementIds];

  const scribe = yield select(state => getScribeById(state, scribeId));

  if (scribe) {
    const scribeClone = cloneDeep(scribe);
    scribeClone.elements.forEach(el => {
      if (elementsToUpdate.includes(el.id)) {
        el.locked = lockFlag;
      }
    });

    yield put(updateScribeAction(scribeClone));
  }
}

function* updateLockOnActiveElements({ type, scribeId, eventTrigger }) {
  const actionCreator = type === LOCK_ACTIVE_ELEMENTS ? lockElementsAction : unlockElementsAction;
  const activeElements = yield select(getActiveElements);
  yield put(actionCreator(activeElements, scribeId, eventTrigger));
}

function* toggleGrid({ scribeId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));

  if (scribe) {
    const scribeClone = cloneDeep(scribe);
    scribeClone.settings.gridlines = !scribeClone.settings.gridlines;
    yield put(updateScribeAction(scribeClone, true));
  }
}

function* updateScribeTitle({ title, scribeId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  if (scribe) {
    const scribeClone = cloneDeep(scribe);
    scribeClone.title = title;

    yield put(updateScribeAction(scribeClone));
  }
}

function* updateElementsTimings({ scribeId, elementIds, property, value }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const scribeClone = cloneDeep(scribe);
  scribeClone.elements.forEach(el => {
    if (elementIds.includes(el.id)) {
      el[property] = value;
    }
  });
  yield put(updateScribeAction(scribeClone));
}

function* prepScribeForEditor({ scribeId }) {
  let scribe = yield select(getScribeById, scribeId);

  // If there is no scribe in state yet, we may be waiting for all
  // the scribes to load - so let wait for them and try again
  if (!scribe) {
    const { loadAllResponse } = yield race({
      loadAllResponse: take(LOAD_SCRIBES_SUCCESS),
      timeout: delay(30000)
    });

    if (loadAllResponse) {
      scribe = yield select(getScribeById, scribeId);
    }
  }

  // If we still dont have a scribe at this point then it's a 404
  if (!scribe) {
    yield put(replace(ROUTE_CODES.HOME));
    yield put(
      showError({
        message: `Unable to load project with id ${scribeId}`,
        description: 'That project is not in your list of available projects'
      })
    );
    return;
  }

  const { elements = [] } = scribe;
  const imageElements = elements.filter(el => el.type === 'Image');

  for (const imageEl of imageElements) {
    yield applyImageRecolor(imageEl);
  }

  const isRenderer = (yield select(state => state.router?.location?.pathname || '')).includes('/render');

  let libraryElements = [];

  if (!isRenderer) {
    for (const imageEl of imageElements) {
      if (imageEl.image.provider === 'library') {
        libraryElements.push(imageEl);
      }
    }
  }

  yield put(uploadImageAssetOnLoadAction(libraryElements, scribeId));

  yield put(prepScribeForEditorSuccess(scribe));
}

function* updateSceneElements({ elements, scribeId, thumbnail, sceneId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const updatedScribe = cloneDeep(scribe);
  const allElements = updatedScribe.elements;

  for (const element of elements) {
    const existingElementIndex = allElements.findIndex(el => el.id === element.id);
    if (existingElementIndex === -1) {
      allElements.push(element);
    } else {
      allElements[existingElementIndex] = element;
    }
  }

  const sceneToUpdateThumbnail = updatedScribe.scenes.find(scene => scene.id === sceneId);
  sceneToUpdateThumbnail.thumbnailImage = thumbnail;
  if (sceneToUpdateThumbnail === updatedScribe.scenes[0]) {
    updatedScribe.thumbnailImage = thumbnail;
  }

  yield put(updateScribeAction(updatedScribe));
}

function* updateSceneThumbnail({ scribeId, thumbnail }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const updatedScribe = cloneDeep(scribe);
  const skipUndoHistory = true;

  const activeScene = updatedScribe.scenes.find(scene => scene.active);
  activeScene.thumbnailImage = thumbnail;
  if (activeScene === updatedScribe.scenes[0]) {
    updatedScribe.thumbnailImage = thumbnail;
  }
  yield put(updateScribeAction(updatedScribe, skipUndoHistory));
}

function* createScene({ scribeId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const sceneIds = yield select(state => state.scribes.selectedSceneIds);

  const { newActiveScribe, sceneId } = createNewScene(scribe, sceneIds);

  yield put(updateScribeAction(newActiveScribe));
  yield put(setSelectedScenesAction([sceneId]));
}

function* duplicateScenes({ scribeId }) {
  const sceneIds = yield select(state => state.scribes.selectedSceneIds);
  const scribe = yield select(state => getScribeById(state, scribeId));

  const { newActiveScribe, clonedSelectedScenes } = duplicateScenesIntoScribe(scribe, sceneIds);

  yield put(updateScribeAction(newActiveScribe));
  yield put(setSelectedScenesAction(clonedSelectedScenes));
}

function* deleteSelectedScenes({ scribeId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const sceneIdsToDelete = yield select(state => state.scribes.selectedSceneIds);

  const updatedScribe = deleteScenesFromScribe(scribe, sceneIdsToDelete);

  yield put(updateScribeAction(updatedScribe));
  yield put(setSelectedScenesAction());
}

function* updateScene({ scene, scribeId }) {
  const activeScribe = yield select(getScribeById, scribeId);

  const newActiveScribe = replaceActiveScene(scene, activeScribe);

  yield put(updateScribeAction(newActiveScribe));
}

function* changeSceneOrder({ scribeId, source, destination, draggableId }) {
  const scribe = yield select(getScribeById, scribeId);
  const selectedSceneIds = yield select(state => state.scribes.selectedSceneIds);

  const updatedScribe = cloneDeep(scribe);
  const { scenes } = updatedScribe;
  const updatedScenes = multiDragAwareReorder(scenes, source, destination, draggableId, selectedSceneIds);
  updatedScribe.scenes = updatedScenes;
  updatedScribe.thumbnailImage = updatedScenes[0].thumbnailImage;

  yield put(updateScribeAction(updatedScribe));
}

function* uploadImageAssetWithRetries(file, filename, maxRetries) {
  const provider = getProviderForType('user');
  let error;
  for (let retryAttempts = 0; retryAttempts < maxRetries; retryAttempts++) {
    try {
      const assetId = yield call(appServices.uploadAsset, file, filename);
      const signedImgUrl = yield call(provider, assetId);
      return {
        assetId,
        signedImgUrl
      };
    } catch (err) {
      error = err;
      if (retryAttempts < maxRetries) {
        yield delay(5000);
      }
    }
  }
  return {
    error
  };
}

function* updateScribeOnUploadSuccess(scribeId, assetId, signedImgUrl, originalAssetId) {
  const activeScribe = yield select(getScribeById, scribeId);
  const skipUndoHistory = true;
  const newActiveScribe = yield call(replaceImageUrlInScribe, activeScribe, originalAssetId, assetId, signedImgUrl);

  if (newActiveScribe !== activeScribe) {
    yield put(updateScribeAction(newActiveScribe, skipUndoHistory));
  }

  const currentActiveScribePast = yield select(state => state.scribes.activeScribePast);
  const activeScribePast = yield replaceImageUrlInHistory(
    currentActiveScribePast,
    assetId,
    signedImgUrl,
    originalAssetId
  );

  const currentActiveScribeFuture = yield select(state => state.scribes.activeScribeFuture);
  const activeScribeFuture = yield replaceImageUrlInHistory(
    currentActiveScribeFuture,
    assetId,
    signedImgUrl,
    originalAssetId
  );

  yield put(updateUndoRedoHistory(activeScribePast, activeScribeFuture));
  yield put(uploadImageAssetSuccess());
}

export function* uploadLibraryImageAsset({ file, filename, scribeId, originalAssetId }) {
  const { assetId, signedImgUrl, error } = yield call(
    uploadImageAssetWithRetries,
    file,
    filename,
    maxLibraryImageUploadRetries
  );
  if (error) {
    console.error(error);
    yield put(uploadImageAssetFailed());
    sendErrorToSentry(error);
    return;
  }
  yield call(updateScribeOnUploadSuccess, scribeId, assetId, signedImgUrl, originalAssetId);
}

function* uploadImageAssetOnLoad({ libraryElements, scribeId }) {
  for (const element of libraryElements) {
    const provider = getProviderForType('library');
    const url = yield call(provider, element.image.assetId ?? element.image.imageId);
    const resp = yield rateLimitedfetch(url);
    const blob = yield resp.blob();

    const { assetId, signedImgUrl, error } = yield call(
      uploadImageAssetWithRetries,
      blob,
      element.image.filename,
      maxImageUploadOnScribeLoadRetries
    );
    if (error) {
      console.error(error);
      yield put(uploadImageAssetFailed());
      sendErrorToSentry(error);
      return;
    }

    yield call(
      updateScribeOnUploadSuccess,
      scribeId,
      assetId,
      signedImgUrl,
      element.image.assetId ?? element.image.imageId
    );
  }
}

export function* uploadUserImageAsset({ file, filename, scribeId, originalAssetId }) {
  const { assetId, signedImgUrl, error } = yield call(
    uploadImageAssetWithRetries,
    file,
    filename,
    maxUserImageUploadRetries
  );

  if (error) {
    console.error(error);

    const activeScribe = yield select(getScribeById, scribeId);
    const skipUndoHistory = true;
    const { newActiveScribe, historyIdsToUpdate } = yield replaceImageUrlBrokenImageInScribe(
      activeScribe,
      originalAssetId
    );

    if (newActiveScribe !== activeScribe) {
      yield put(updateScribeAction(newActiveScribe, skipUndoHistory));
    }

    const currentActiveScribePast = yield select(state => state.scribes.activeScribePast);
    const activeScribePast = yield replaceBrokenImageUrlInHistory(currentActiveScribePast, historyIdsToUpdate);

    const currentActiveScribeFuture = yield select(state => state.scribes.activeScribeFuture);
    const activeScribeFuture = yield replaceBrokenImageUrlInHistory(currentActiveScribeFuture, historyIdsToUpdate);

    yield put(updateUndoRedoHistory(activeScribePast, activeScribeFuture));
    yield put(uploadImageAssetFailed());

    yield put(
      showError({
        message: `Uh-oh! Your image failed to upload into ${APPLICATION_DISPLAY_NAME}, but never fear, just try uploading your image again.`,
        description: 'Happy animating!'
      })
    );

    sendErrorToSentry(error);
    return;
  }

  yield call(updateScribeOnUploadSuccess, scribeId, assetId, signedImgUrl, originalAssetId);
}

function* scribeSagas() {
  yield throttle(2000, UPDATE_SCRIBE, updateScribe);
  yield takeLatest(ADD_SCRIBE_REQUESTED, addScribe);
  yield takeEvery(CREATE_ELEMENT, createElement);
  yield takeEvery(CREATE_TEXT_ELEMENT, createTextElement);
  yield takeEvery(CREATE_SHAPE_ELEMENT, createShapeElement);
  yield takeEvery(CREATE_IMAGE_ELEMENT, createImageElement);
  yield takeEvery(CREATE_SCENE, createScene);
  yield takeLatest(EDITOR_PASTE, handleEditorPaste);
  yield takeLatest(DELETE_ELEMENT_BY_ID, deleteElementById);
  yield takeLatest(EDITOR_CUT, cut);
  yield takeLatest(EDITOR_UNDO, undo);
  yield takeLatest(EDITOR_REDO, redo);
  yield takeLatest(MOVE_ELEMENT_ON_TIMELINE, moveElementOnTimeline);
  yield takeLatest(DELETE_ACTIVE_ELEMENT, deleteActiveElement);
  yield takeLatest(LOAD_SCRIBES, loadScribes);
  yield takeLatest(DELETE_SCRIBE, deleteScribe);
  yield takeLatest(SORT_SCRIBES, sortScribes);
  yield takeLatest(DELETE_MULTIPLE_ELEMENTS_BY_ID, deleteMultipleElementsById);
  yield takeLatest(SAVE_AUDIO, saveAudio);
  yield takeLatest(DELETE_AUDIO, deleteAudio);
  yield takeLatest(UPDATE_AUDIO_CONFIG, updateAudioConfig);
  yield takeLatest(LOAD_SCRIBE_BY_ID, loadScribeById);
  yield takeLatest(LOAD_CURSORS, loadCursors);
  yield takeLatest(UPDATE_SCRIBE_CURSOR, updateScribeCursor);
  yield takeLatest([LOCK_ELEMENTS, UNLOCK_ELEMENTS], updateElementsLock);
  yield takeLatest([UNLOCK_ACTIVE_ELEMENTS, LOCK_ACTIVE_ELEMENTS], updateLockOnActiveElements);
  yield takeLatest(TOGGLE_GRID, toggleGrid);
  yield takeLatest(UPDATE_SCRIBE_TITLE, updateScribeTitle);
  yield takeLatest(SET_ELEMENTS_TIMINGS, updateElementsTimings);
  yield takeLatest(SET_ACTIVE_SCRIBE, prepScribeForEditor);
  yield takeLatest(UPDATE_SCENE_ELEMENTS, updateSceneElements);
  yield takeLatest(UPDATE_SCENE_THUMBNAIL, updateSceneThumbnail);
  yield takeLatest(DUPLICATE_SELECTED_SCENES, duplicateScenes);
  yield takeLatest(DELETE_SELECTED_SCENES, deleteSelectedScenes);
  yield takeLatest(UPDATE_SCENE, updateScene);
  yield takeLatest(CHANGE_SCENE_ORDER, changeSceneOrder);
  yield takeEvery(UPLOAD_IMAGE_ASSET_ON_LOAD, uploadImageAssetOnLoad);
  yield takeEvery(UPLOAD_LIBRARY_IMAGE_ASSET, uploadLibraryImageAsset);
  yield takeEvery(UPLOAD_USER_IMAGE_ASSET, uploadUserImageAsset);
  yield fork(duplicateScribeSagas);
}

export default scribeSagas;
